import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core';
import { SketchPicker } from 'react-color';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Sticky, StickyContainer } from 'react-sticky';
import { APP_PATH_DASHBOARD, PLAN_TYPE } from '../../config';
import {
  getStripePriceIdSelector,
  getStripePriceSelector,
  stripePublishableKeySelector
} from '../../redux/features/configuration/configurationSlice';
import {
  createPayment,
  isLoadingSelector,
  subscriptionCleared,
  subscriptionSelector
} from '../../redux/features/invoice/invoicesSlice';
import {
  activeBenefitsByJobIdSelector,
  fetchActiveBenefits
} from '../../redux/features/benefit/benefitSlice';
import { getLocalStorageAuthentication } from '../../lib/auth';
import { messageUpdated } from '../../redux/features/app/appSlice';
import { Severity } from '../../components/Message/Message';
import CardForm from '../CardForm';
import { useGoToPath } from '../../hooks/useGoToPath';
import {
  currentEnrichedJobSelector,
  fetchEnrichedJobById
} from '../../redux/features/job/jobsSlice';
import PlanPackageView from '../SubscriptionPlanView/PlanPackageView';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  header: {
    marginBottom: theme.spacing(3),
    textAlign: 'center'
  },
  headerSectionView: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  previewTitle: {
    textAlign: 'center'
  },
  card: {
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1]
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1)
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  optionRowDisabled: {
    opacity: 0.55
  },
  optionLabel: {
    fontWeight: 600
  },
  optionPrice: {
    fontWeight: 600
  },
  stickyGroup: {
    marginTop: theme.spacing(2)
  },
  colorPicker: {
    marginTop: theme.spacing(2)
  },
  summaryBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
  },
  laterButton: {
    marginTop: theme.spacing(3),
    textTransform: 'none',
    fontSize: '1rem'
  }
}));

// Note: These arrays will be populated with prices from Redux in the component
const ADDON_OPTIONS_BASE = [
  {
    id: PLAN_TYPE.HIGHLIGHT_YELLOW,
    label: 'Yellow highlight',
    description: 'Boost visibility with a bold yellow highlight.'
  },
  {
    id: PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR,
    label: 'Brand color highlight',
    description: 'Stand out with your brand color highlight.'
  },
  {
    id: PLAN_TYPE.GEO_LOCK,
    label: 'Geo-lock',
    description: 'Limit applicants to allowed regions.'
  }
];

const STICKY_OPTIONS_BASE = [
  {
    id: PLAN_TYPE.STICKY_TOP_24_HOURS,
    label: 'Sticky for 24 hours',
    description: 'Pin the job for the next 24 hours.'
  },
  {
    id: PLAN_TYPE.STICKY_TOP_1_WEEK,
    label: 'Sticky for 1 week',
    description: 'Stay pinned for a full week.'
  },
  {
    id: PLAN_TYPE.STICKY_TOP_1_MONTH,
    label: 'Sticky for 1 month',
    description: 'Maximize visibility for 30 days.'
  }
];

const JobAddonsView = () => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const { handleGoToPath } = useGoToPath(APP_PATH_DASHBOARD);
  const subscription = useSelector(subscriptionSelector);
  const isLoading = useSelector(isLoadingSelector);
  const currentEnrichedJob = useSelector(currentEnrichedJobSelector);

  const [highlightYellow, setHighlightYellow] = useState(false);
  const [highlightBrandColor, setHighlightBrandColor] = useState(false);
  const [geoLockEnabled, setGeoLockEnabled] = useState(false);
  const [stickyOption, setStickyOption] = useState('');
  const [brandColor, setBrandColor] = useState('#FF6B6B');
  const [stripePromise, setStripePromise] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const jobId = searchParams.get('jobId');

  const activeBenefits = useSelector((state) =>
    activeBenefitsByJobIdSelector(state, jobId)
  );

  // Get Stripe publishable key from Redux
  const stripePublishableKey = useSelector(stripePublishableKeySelector);

  // Initialize Stripe promise when key is available
  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  // Fetch pricing from Redux for add-ons
  const highlightYellowPrice = useSelector(
    getStripePriceSelector(PLAN_TYPE.HIGHLIGHT_YELLOW)
  );
  const highlightYellowPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.HIGHLIGHT_YELLOW)
  );

  const highlightBrandPrice = useSelector(
    getStripePriceSelector(PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR)
  );
  const highlightBrandPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR)
  );

  const geoLockPrice = useSelector(getStripePriceSelector(PLAN_TYPE.GEO_LOCK));
  const geoLockPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.GEO_LOCK)
  );

  const sticky24hPrice = useSelector(
    getStripePriceSelector(PLAN_TYPE.STICKY_TOP_24_HOURS)
  );
  const sticky24hPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.STICKY_TOP_24_HOURS)
  );

  const sticky1wPrice = useSelector(
    getStripePriceSelector(PLAN_TYPE.STICKY_TOP_1_WEEK)
  );
  const sticky1wPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.STICKY_TOP_1_WEEK)
  );

  const sticky1mPrice = useSelector(
    getStripePriceSelector(PLAN_TYPE.STICKY_TOP_1_MONTH)
  );
  const sticky1mPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.STICKY_TOP_1_MONTH)
  );

  const ADDON_OPTIONS = [
    {
      ...ADDON_OPTIONS_BASE[0],
      price: highlightYellowPrice,
      priceId: highlightYellowPriceId
    },
    {
      ...ADDON_OPTIONS_BASE[1],
      price: highlightBrandPrice,
      priceId: highlightBrandPriceId
    },
    { ...ADDON_OPTIONS_BASE[2], price: geoLockPrice, priceId: geoLockPriceId }
  ];

  const STICKY_OPTIONS = [
    {
      ...STICKY_OPTIONS_BASE[0],
      price: sticky24hPrice,
      priceId: sticky24hPriceId
    },
    {
      ...STICKY_OPTIONS_BASE[1],
      price: sticky1wPrice,
      priceId: sticky1wPriceId
    },
    {
      ...STICKY_OPTIONS_BASE[2],
      price: sticky1mPrice,
      priceId: sticky1mPriceId
    }
  ];

  const activeAddons = useMemo(() => {
    const benefitTypeToPlanId = {
      HIGHLIGHT_YELLOW: PLAN_TYPE.HIGHLIGHT_YELLOW,
      HIGHLIGHT_BRAND: PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR,
      GEO_LOCK: PLAN_TYPE.GEO_LOCK,
      STICKY_24H: PLAN_TYPE.STICKY_TOP_24_HOURS,
      STICKY_1W: PLAN_TYPE.STICKY_TOP_1_WEEK,
      STICKY_1M: PLAN_TYPE.STICKY_TOP_1_MONTH
    };

    const activeMap = {};
    activeBenefits.forEach((benefit) => {
      const planId = benefitTypeToPlanId[benefit.benefitType];
      if (planId) {
        activeMap[planId] = benefit;
      }
    });

    return activeMap;
  }, [activeBenefits]);

  useEffect(() => {
    dispatch(subscriptionCleared());
  }, [dispatch]);

  useEffect(() => {
    if (jobId) {
      dispatch(fetchEnrichedJobById({ jobId }));
    }
  }, [dispatch, jobId]);

  useEffect(() => {
    if (jobId) {
      dispatch(fetchActiveBenefits(jobId));
    }
  }, [dispatch, jobId]);

  useEffect(() => {
    if (activeAddons[PLAN_TYPE.HIGHLIGHT_YELLOW]) {
      setHighlightYellow(false);
    }
    if (activeAddons[PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR]) {
      setHighlightBrandColor(false);
    }
    if (activeAddons[PLAN_TYPE.GEO_LOCK]) {
      setGeoLockEnabled(false);
    }
    if (activeAddons[Number(stickyOption)]) {
      setStickyOption('');
    }
  }, [activeAddons, stickyOption]);

  const jobTitle = currentEnrichedJob?.job?.title || 'this job';
  const companyForPreview = currentEnrichedJob?.company || {
    id: 'preview',
    name: 'Your Company Name',
    description: 'Preview your job as candidates will see it.',
    website: 'https://yourcompany.com'
  };
  const jobForPreview = currentEnrichedJob?.job || {
    id: 'preview',
    title: 'Senior Full-Stack Developer',
    category: 'Software Development',
    type: 'Full-Time',
    description:
      'This preview updates as you select add-ons for your job post.',
    salaryFrom: 80000,
    salaryTo: 120000,
    currency: 'USD',
    location: 'Remote - Worldwide',
    regionalRestrictions: [],
    status: 'ACTIVE'
  };
  const fileLogoUrl = currentEnrichedJob?.file?.publicUrl || '';
  const mockResultsDataPreview = useMemo(() => {
    const baseCompany = companyForPreview;
    const file = fileLogoUrl ? { publicUrl: fileLogoUrl } : null;
    const sampleJobs = [
      {
        id: 'sample-1',
        title: 'Product Designer',
        category: 'Design',
        type: 'Full-Time',
        description: 'Design intuitive interfaces for our remote product team.',
        salaryFrom: 70000,
        salaryTo: 105000,
        currency: 'USD',
        location: 'Remote - Americas',
        regionalRestrictions: [],
        status: 'ACTIVE'
      },
      {
        id: 'sample-2',
        title: 'Frontend Engineer',
        category: 'Software Development',
        type: 'Contract',
        description:
          'Build delightful user experiences with modern web tooling.',
        salaryFrom: 85000,
        salaryTo: 125000,
        currency: 'USD',
        location: 'Remote - Europe',
        regionalRestrictions: [],
        status: 'ACTIVE'
      },
      {
        id: 'sample-3',
        title: 'Data Analyst',
        category: 'Data',
        type: 'Full-Time',
        description: 'Turn product data into insights for distributed teams.',
        salaryFrom: 65000,
        salaryTo: 95000,
        currency: 'USD',
        location: 'Remote - Worldwide',
        regionalRestrictions: [],
        status: 'ACTIVE'
      }
    ];

    return sampleJobs.map((job) => ({
      job,
      company: baseCompany,
      file,
      showMyLogo: true,
      showOnTopDemo: false,
      highlightWithYellow: false,
      brandColor: null,
      highlightWithBrandColor: false
    }));
  }, [companyForPreview, fileLogoUrl]);

  const selectedAddons = useMemo(() => {
    const selected = [];
    if (highlightYellow) {
      selected.push(ADDON_OPTIONS[0]);
    }
    if (highlightBrandColor) {
      selected.push(ADDON_OPTIONS[1]);
    }
    if (geoLockEnabled) {
      selected.push(ADDON_OPTIONS[2]);
    }
    const sticky = STICKY_OPTIONS.find(
      (option) => option.id === Number(stickyOption)
    );
    if (sticky) {
      selected.push(sticky);
    }
    return selected;
  }, [
    ADDON_OPTIONS,
    STICKY_OPTIONS,
    highlightYellow,
    highlightBrandColor,
    geoLockEnabled,
    stickyOption
  ]);

  const totalPrice = selectedAddons.reduce(
    (sum, addon) => sum + addon.price,
    0
  );
  const showOnTopDemo = Boolean(stickyOption);

  const handleToggleAddon = (addonId) => {
    if (activeAddons[addonId]) {
      return;
    }
    if (addonId === PLAN_TYPE.HIGHLIGHT_YELLOW) {
      setHighlightYellow((prev) => {
        if (!prev) {
          setHighlightBrandColor(false);
        }
        return !prev;
      });
      return;
    }
    if (addonId === PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR) {
      setHighlightBrandColor((prev) => {
        if (!prev) {
          setHighlightYellow(false);
        }
        return !prev;
      });
      return;
    }
    if (addonId === PLAN_TYPE.GEO_LOCK) {
      setGeoLockEnabled((prev) => !prev);
    }
  };

  const handlePayment = (paymentMethod) => {
    if (!jobId) {
      dispatch(
        messageUpdated({
          text: 'Job ID is required to purchase add-ons.',
          severity: Severity.ERROR
        })
      );
      return;
    }

    if (selectedAddons.length === 0) {
      dispatch(
        messageUpdated({
          text: 'Select at least one add-on to continue.',
          severity: Severity.WARNING
        })
      );
      return;
    }

    const localAuth = getLocalStorageAuthentication();
    const items = selectedAddons.map((addon) => ({
      price: addon.priceId
    }));

    const metadata = {
      highlightYellow,
      highlightBrandColor,
      brandColor: highlightBrandColor ? brandColor : null,
      geoLockEnabled,
      selectedPlans: selectedAddons.map((addon) => ({
        id: addon.id,
        name: addon.label
      }))
    };

    dispatch(
      createPayment({
        userId: localAuth.userId,
        email: localAuth?.email,
        payment_method: paymentMethod.id,
        currency: 'usd',
        items,
        job_id: jobId,
        metadata,
        plan_id: null,
        successMessage: '✅ Payment received! Applying add-ons to your job...'
      })
    );
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) {
      return 'Active · no expiry';
    }
    const date = new Date(expiresAt);
    if (Number.isNaN(date.getTime())) {
      return 'Active';
    }
    return `Active · expires ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })}`;
  };

  const getActiveLabel = (benefit) => {
    if (!benefit) return '';
    const color = benefit.metadata?.brandColor;
    if (color) {
      const expiry = formatExpiry(benefit.expiresAt).replace('Active · ', '');
      return `Active · ${color.toUpperCase()} · ${expiry}`;
    }
    return formatExpiry(benefit.expiresAt);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.header} elevation={0}>
        <Typography variant="h2" gutterBottom>
          Job Add-ons
        </Typography>
        <Typography className={classes.sectionSubtitle}>
          Add visibility and targeting upgrades for <strong>{jobTitle}</strong>.
        </Typography>
      </Paper>

      <StickyContainer>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Highlights & Targeting
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Choose one highlight style and optional geo-lock.
                </Typography>
                {ADDON_OPTIONS.map((addon) => {
                  const activeBenefit = activeAddons[addon.id];
                  const isDisabled = Boolean(activeBenefit);
                  const description = activeBenefit
                    ? getActiveLabel(activeBenefit)
                    : addon.description;

                  return (
                    <div
                      key={addon.id}
                      className={
                        isDisabled
                          ? `${classes.optionRow} ${classes.optionRowDisabled}`
                          : classes.optionRow
                      }>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              addon.id === PLAN_TYPE.HIGHLIGHT_YELLOW
                                ? highlightYellow
                                : addon.id === PLAN_TYPE.HIGHLIGHT_COMPANY_COLOR
                                ? highlightBrandColor
                                : geoLockEnabled
                            }
                            onChange={() => handleToggleAddon(addon.id)}
                            color="primary"
                            disabled={isDisabled}
                          />
                        }
                        label={
                          <Box>
                            <Typography className={classes.optionLabel}>
                              {addon.label}
                            </Typography>
                            <Typography className={classes.helperText}>
                              {description}
                            </Typography>
                          </Box>
                        }
                      />
                      <Typography className={classes.optionPrice}>
                        ${addon.price}
                      </Typography>
                    </div>
                  );
                })}

                {highlightBrandColor && (
                  <Box className={classes.colorPicker}>
                    <Typography className={classes.helperText}>
                      Pick your brand color:
                    </Typography>
                    <SketchPicker
                      color={brandColor}
                      onChange={(color) => setBrandColor(color.hex)}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            <Box marginTop={3}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Sticky placement
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Pin your job at the top of the board for a set duration.
                  </Typography>
                  <RadioGroup
                    value={stickyOption}
                    onChange={(event) => setStickyOption(event.target.value)}
                    className={classes.stickyGroup}>
                    {STICKY_OPTIONS.map((option) => {
                      const activeBenefit = activeAddons[option.id];
                      const isDisabled = Boolean(activeBenefit);
                      const description = activeBenefit
                        ? formatExpiry(activeBenefit.expiresAt)
                        : option.description;

                      return (
                        <div
                          key={option.id}
                          className={
                            isDisabled
                              ? `${classes.optionRow} ${classes.optionRowDisabled}`
                              : classes.optionRow
                          }>
                          <FormControlLabel
                            value={String(option.id)}
                            control={<Radio color="primary" />}
                            label={
                              <Box>
                                <Typography className={classes.optionLabel}>
                                  {option.label}
                                </Typography>
                                <Typography className={classes.helperText}>
                                  {description}
                                </Typography>
                              </Box>
                            }
                            disabled={isDisabled}
                          />
                          <Typography className={classes.optionPrice}>
                            ${option.price}
                          </Typography>
                        </div>
                      );
                    })}
                    <FormControlLabel
                      value=""
                      control={<Radio color="primary" />}
                      label="No sticky placement"
                    />
                  </RadioGroup>

                  <Box className={classes.summaryBox}>
                    <div className={classes.summaryRow}>
                      <Typography className={classes.optionLabel}>
                        Total today
                      </Typography>
                      <Typography className={classes.optionPrice}>
                        ${totalPrice}
                      </Typography>
                    </div>
                    <Typography className={classes.helperText}>
                      Add-ons apply immediately after payment.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box marginTop={3}>
              {!stripePromise ? (
                <Box textAlign="center" p={3}>
                  <Typography>Loading payment configuration...</Typography>
                </Box>
              ) : (
                <Elements stripe={stripePromise}>
                  <CardForm
                    totalPrice={totalPrice}
                    emailInvoice={getLocalStorageAuthentication()?.email || ''}
                    subscription={subscription}
                    isLoading={isLoading}
                    onClickPay={handlePayment}
                    onPaymentSuccess={handleGoToPath}
                    setMessageApp={(text, severity) =>
                      dispatch(messageUpdated({ text, severity }))
                    }
                    payLabel="Buy Add-ons Securely"
                  />
                </Elements>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Sticky>
              {({ style }) => (
                <div style={style}>
                  <Paper className={classes.headerSectionView} elevation={0}>
                    <Typography variant="h3" className={classes.previewTitle}>
                      Preview
                    </Typography>
                  </Paper>
                  <PlanPackageView
                    fileLogoURL={fileLogoUrl}
                    company={companyForPreview}
                    jobToPreview={jobForPreview}
                    jobResults={mockResultsDataPreview}
                    isPreview
                    showOnTopDemo={showOnTopDemo}
                    showMyLogo
                    highlightWithYellow={highlightYellow}
                    brandColor={brandColor}
                    highlightWithBrandColor={highlightBrandColor}
                  />
                </div>
              )}
            </Sticky>
          </Grid>
        </Grid>
      </StickyContainer>
      <Button
        variant="contained"
        color="default"
        className={classes.laterButton}
        onClick={handleGoToPath}>
        I'll do it later
      </Button>
    </div>
  );
};

export default JobAddonsView;
